import { ChevronDown } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  SurfaceTabs,
  isSurfaceTabActive,
  type SurfaceTab,
} from "@triedge/domain-surface-primitives";
import { AppDialogFrame } from "../overlays/AppDialogFrame";

export type ModuleSectionNavItem = SurfaceTab;

type ModuleSectionNavTestIds = {
  root?: string;
  itemPrefix?: string;
  itemIds?: Record<string, string>;
  mobileTrigger?: string;
  mobileDialog?: string;
  mobileItemPrefix?: string;
  mobileItemIds?: Record<string, string>;
};

export type ModuleSectionNavProps = {
  items: ModuleSectionNavItem[];
  activeId?: string | null;
  ariaLabel: string;
  mobileMenuTitle?: string;
  compact?: boolean;
  renderDesktop?: boolean;
  desktopClassName?: string;
  desktopBordered?: boolean;
  mobileTriggerFullWidth?: boolean;
  triggerClassName?: string;
  dialogMaxWidthClassName?: string;
  mobileCloseLabel?: string;
  testIds?: ModuleSectionNavTestIds;
};

const DEFAULT_BREAKPOINT_DESKTOP = "hidden lg:flex";
const DEFAULT_BREAKPOINT_MOBILE = "lg:hidden";

const joinClassNames = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const normalizeTestIds = (
  testIds?: ModuleSectionNavTestIds,
): Required<ModuleSectionNavTestIds> => ({
  root: testIds?.root ?? "module-section-nav",
  itemPrefix: testIds?.itemPrefix ?? "module-section-nav-item",
  itemIds: testIds?.itemIds ?? {},
  mobileTrigger: testIds?.mobileTrigger ?? "module-section-nav-mobile-trigger",
  mobileDialog: testIds?.mobileDialog ?? "module-section-nav-mobile-dialog",
  mobileItemPrefix:
    testIds?.mobileItemPrefix ?? "module-section-nav-mobile-item",
  mobileItemIds: testIds?.mobileItemIds ?? {},
});

export function ModuleSectionNav({
  items,
  activeId,
  ariaLabel,
  mobileMenuTitle = "Navigate to...",
  compact,
  renderDesktop = true,
  desktopClassName,
  desktopBordered = true,
  mobileTriggerFullWidth = true,
  triggerClassName,
  dialogMaxWidthClassName = "max-w-3xl",
  mobileCloseLabel,
  testIds,
}: ModuleSectionNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const desktopNavWrapperRef = useRef<HTMLDivElement | null>(null);
  const resolvedTestIds = normalizeTestIds(testIds);

  const resolvedActiveItem = useMemo(() => {
    if (activeId) {
      return items.find((item) => item.id === activeId) ?? items[0] ?? null;
    }
    return (
      items.find((item) => isSurfaceTabActive(item, location.pathname)) ??
      items[0] ??
      null
    );
  }, [activeId, items, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    const nav = desktopNavWrapperRef.current?.querySelector<HTMLElement>(
      `[data-testid="${resolvedTestIds.root}"]`,
    );
    if (!nav) {
      return;
    }
    if (desktopBordered) {
      nav.classList.add("border-b");
      return;
    }
    nav.classList.remove("border-b");
  }, [compact, desktopBordered, location.pathname, renderDesktop, resolvedTestIds.root]);

  const desktopVisibilityClass =
    compact == null ? DEFAULT_BREAKPOINT_DESKTOP : compact ? "hidden" : "flex";
  const mobileVisibilityClass =
    compact == null ? DEFAULT_BREAKPOINT_MOBILE : compact ? "block" : "hidden";

  return (
    <>
      {renderDesktop ? (
        <div ref={desktopNavWrapperRef}>
          <SurfaceTabs
            items={items.map((item) => ({
              ...item,
              testId:
                item.testId ??
                resolvedTestIds.itemIds[item.id] ??
                `${resolvedTestIds.itemPrefix}-${item.id}`,
            }))}
            activeId={activeId}
            ariaLabel={ariaLabel}
            testId={resolvedTestIds.root}
            className={joinClassNames(desktopVisibilityClass, desktopClassName)}
          />
        </div>
      ) : null}

      <div className={mobileVisibilityClass}>
        <button
          type="button"
          data-testid={resolvedTestIds.mobileTrigger}
          aria-label={mobileMenuTitle}
          onClick={() => setMobileOpen(true)}
          className={joinClassNames(
            "yst-motion-surface flex items-center justify-between gap-3 rounded-[10px] border border-border bg-surface-light px-4 py-2.5 text-left shadow-card hover:border-primary/25 hover:bg-muted/70",
            mobileTriggerFullWidth ? "w-full" : "max-sm:w-full",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-irongrey">
            {resolvedActiveItem?.icon ? (
              <resolvedActiveItem.icon size={16} />
            ) : null}
            <span className="truncate">
              {resolvedActiveItem?.label ?? "Select section"}
            </span>
          </span>
          <span className="flex items-center gap-2">
            {resolvedActiveItem?.badge != null ? (
              <span className="rounded-full bg-[var(--strawberry)] px-2 py-0.5 text-[10px] font-bold text-white">
                {resolvedActiveItem.badge}
              </span>
            ) : null}
            <ChevronDown size={16} className="text-irongrey" />
          </span>
        </button>
      </div>

      <AppDialogFrame
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        title={mobileMenuTitle}
        testId={resolvedTestIds.mobileDialog}
        maxWidthClassName={dialogMaxWidthClassName}
        insetMode="viewport"
        closeLabel={mobileCloseLabel}
        headerClassName="bg-muted/70"
        titleClassName="text-[10px] font-semibold uppercase tracking-widest text-irongrey/45"
        bodyPaddingClassName="px-0 py-2"
      >
        <nav aria-label={ariaLabel}>
          {items.map((item) => {
            const active = resolvedActiveItem?.id === item.id;
            const Icon = item.icon;
            const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
              if (item.disabled) {
                event.preventDefault();
                return;
              }
              setMobileOpen(false);
              if (
                item.reloadDocument ||
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.altKey ||
                event.ctrlKey ||
                event.shiftKey
              ) {
                return;
              }
              event.preventDefault();
              navigate(item.to);
            };
            return (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.end}
                data-testid={
                  resolvedTestIds.mobileItemIds[item.id] ??
                  `${resolvedTestIds.mobileItemPrefix}-${item.id}`
                }
                aria-disabled={item.disabled ? "true" : undefined}
                onClick={handleClick}
                className={joinClassNames(
                  "yst-motion-color flex items-center gap-3 px-6 py-3.5 text-sm",
                  active
                    ? "border-l-4 border-aquamarine bg-aquamarine/10 font-semibold text-jungle"
                    : "font-medium text-irongrey hover:bg-muted/70",
                  item.disabled ? "pointer-events-none opacity-50" : "",
                )}
              >
                {Icon ? <Icon size={18} /> : null}
                <span className="flex-1">{item.label}</span>
                {item.badge != null ? (
                  <span className="rounded-full bg-[var(--strawberry)] px-2 py-0.5 text-[10px] font-bold text-white">
                    {item.badge}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
      </AppDialogFrame>
    </>
  );
}
